import React, { createContext, useContext, useRef, useCallback } from 'react';

// Define the event name type
export type EventName = string;

// Define the event data type
export interface EventData {
    message: string;
}

type EventDataMap = {
    [key: string]: any;
};

type EventCallback<E extends EventName> = (data: EventDataMap[E]) => void;

interface ListenerEntry<E extends EventName> {
    eventName: E;
    callback: EventCallback<E>;
}

interface EventNotifierContextType {
    subscribe: <E extends EventName>(eventName: E, callback: EventCallback<E>) => () => void;
    emit: <E extends EventName>(eventName: E, data: EventDataMap[E]) => void;
}

const EventNotifierContext = createContext<EventNotifierContextType | undefined>(undefined);

export const EventNotifierProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const listeners = useRef<Set<ListenerEntry<EventName>>>(new Set());

    const subscribe = useCallback(<E extends EventName>(eventName: E, callback: EventCallback<E>): (() => void) => {
        const listener: ListenerEntry<E> = { eventName, callback };
        listeners.current.add(listener as unknown as ListenerEntry<EventName>); // Type assertion for the Set

        return () => {
            listeners.current.delete(listener as unknown as ListenerEntry<EventName>);
        };
    }, []);

    const emit = useCallback(<E extends EventName>(eventName: E, data: EventDataMap[E]): void => {
        listeners.current.forEach(listener => {
            if (listener.eventName === eventName) {
                (listener.callback as EventCallback<E>)(data);
            }
        });
    }, []);

    // Use useRef to keep the context value stable across renders
    // Initialize with the stable functions
    const contextValue = useRef<EventNotifierContextType>({ subscribe, emit });

    // If you later add state to the provider and memoize `subscribe` or `emit`
    // with `useCallback` that depends on that state, you would update this:
    // const contextValue = useMemo(() => ({ subscribe, emit }), [subscribe, emit]);

    return (
        <EventNotifierContext.Provider value={contextValue.current}>
            {children}
        </EventNotifierContext.Provider>
    );
};

export const useEventNotifier = (): EventNotifierContextType => {
    const context = useContext(EventNotifierContext);
    if (context === undefined) {
        // Throw an error if the hook is used outside the provider
        throw new Error('useEventNotifier must be used within an EventNotifierProvider');
    }
    return context;
};